import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
var _excluded = ["context"];
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
import cx from 'classnames';
import React, { forwardRef, useState } from 'react';
import { TypeaheadContext, useTypeahead } from '../../core';
import { useOverlay } from '../../hooks';
import ClearButton from '../ClearButton';
import Loader from '../Loader';
import Token from '../Token';
import TypeaheadInputMulti from '../TypeaheadInputMulti';
import TypeaheadInputSingle from '../TypeaheadInputSingle';
import TypeaheadMenu from '../TypeaheadMenu';
import usePagination from './usePagination';
import { getOptionLabel, isFunction, isSizeLarge, preventInputBlur } from '../../utils';
var defaultRenderMenu = function defaultRenderMenu(results, menuProps, props) {
  return /*#__PURE__*/React.createElement(TypeaheadMenu, _extends({}, menuProps, {
    labelKey: props.labelKey,
    options: results,
    text: props.text
  }));
};
var defaultRenderToken = function defaultRenderToken(option, props, idx) {
  return /*#__PURE__*/React.createElement(Token, {
    disabled: props.disabled,
    key: idx,
    onRemove: props.onRemove,
    option: option,
    tabIndex: props.tabIndex
  }, getOptionLabel(option, props.labelKey));
};
var Typeahead = /*#__PURE__*/forwardRef(function (props, ref) {
  var _useTypeahead = useTypeahead(props, ref),
    context = _useTypeahead.context,
    rest = _objectWithoutProperties(_useTypeahead, _excluded);
  var children = props.children,
    clearButton = props.clearButton,
    disabled = props.disabled,
    emptyLabel = props.emptyLabel,
    isLoading = props.isLoading,
    isInvalid = props.isInvalid,
    isValid = props.isValid,
    maxHeight = props.maxHeight,
    _props$maxResults = props.maxResults,
    maxResults = _props$maxResults === void 0 ? 100 : _props$maxResults,
    multiple = props.multiple,
    newSelectionPrefix = props.newSelectionPrefix,
    _props$paginate = props.paginate,
    paginate = _props$paginate === void 0 ? true : _props$paginate,
    paginationText = props.paginationText,
    placeholder = props.placeholder,
    renderMenuItemChildren = props.renderMenuItemChildren,
    size = props.size;
  var getInputProps = rest.getInputProps,
    getMenuProps = rest.getMenuProps,
    isMenuShown = rest.isMenuShown,
    labelKey = rest.labelKey,
    onClear = rest.onClear,
    onItemSelect = rest.onItemSelect,
    onRemove = rest.onRemove,
    selected = rest.selected,
    text = rest.text;
  var _usePagination = usePagination({
      isMenuShown: isMenuShown,
      labelKey: labelKey,
      maxResults: maxResults,
      onPaginate: props.onPaginate,
      paginate: paginate,
      results: rest.results,
      text: text
    }),
    onPaginate = _usePagination.onPaginate,
    results = _usePagination.results;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    referenceElement = _useState2[0],
    setReferenceElement = _useState2[1];

  // Menu
  var overlayProps = useOverlay(referenceElement, props);
  var renderMenu = props.renderMenu || defaultRenderMenu;
  var menuProps = _objectSpread(_objectSpread({
    emptyLabel: emptyLabel,
    maxHeight: maxHeight,
    newSelectionPrefix: newSelectionPrefix,
    onItemSelect: onItemSelect,
    onPaginate: onPaginate,
    paginationText: paginationText,
    renderMenuItemChildren: renderMenuItemChildren
  }, overlayProps), getMenuProps());
  function renderInput() {
    // TODO: Add warnings for conflicting input props.
    var inputProps = _objectSpread(_objectSpread({}, getInputProps(_objectSpread(_objectSpread({}, props.inputProps), {}, {
      disabled: disabled,
      placeholder: placeholder
    }))), {}, {
      referenceElementRef: setReferenceElement
    });
    if (props.renderInput) {
      return props.renderInput(inputProps, rest);
    }
    var commonProps = _objectSpread(_objectSpread({}, inputProps), {}, {
      isInvalid: isInvalid,
      isValid: isValid,
      size: size
    });
    if (!multiple) {
      return /*#__PURE__*/React.createElement(TypeaheadInputSingle, commonProps);
    }
    var renderToken = props.renderToken || defaultRenderToken;
    var tokenProps = _objectSpread(_objectSpread({}, commonProps), {}, {
      labelKey: labelKey,
      onRemove: onRemove
    });
    return /*#__PURE__*/React.createElement(TypeaheadInputMulti, _extends({}, commonProps, {
      placeholder: selected.length ? '' : inputProps.placeholder,
      selected: selected
    }), selected.map(function (option, idx) {
      return renderToken(option, tokenProps, idx);
    }));
  }
  var auxContent;
  if (isLoading) {
    auxContent = /*#__PURE__*/React.createElement(Loader, null);
  } else if (clearButton && !disabled && selected !== null && selected !== void 0 && selected.length) {
    auxContent = /*#__PURE__*/React.createElement(ClearButton, {
      onClick: onClear,
      onMouseDown: preventInputBlur,
      size: size
    });
  }
  return /*#__PURE__*/React.createElement(TypeaheadContext.Provider, {
    value: context
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('rbt', {
      'has-aux': !!auxContent,
      'is-invalid': isInvalid,
      'is-valid': isValid
    }, props.className),
    style: _objectSpread(_objectSpread({}, props.style), {}, {
      outline: 'none',
      position: 'relative'
    }),
    tabIndex: -1
  }, renderInput(), isMenuShown && renderMenu(results, menuProps, rest), auxContent && /*#__PURE__*/React.createElement("div", {
    className: cx('rbt-aux', {
      'rbt-aux-lg': isSizeLarge(size)
    })
  }, auxContent), isFunction(children) ? children(rest) : children));
});
export default Typeahead;